(() => {
  'use strict';
  if (typeof window !== 'undefined' && window.KidAccountFlow === 'shopify-email-phone') return;

  const TOKEN_KEY = 'kid_sms_account_v1';
  const RETURN_KEY = 'kid_sms_checkout_return_v1';
  const RETURN_MAX_AGE_MS = 30 * 60 * 1000;
  const SESSION_URL = '/apps/kid-sms-account/session/status';
  const LOGIN_PATH = '/pages/member-login';
  const CHECKOUT_PATH = '/checkout';
  const allowedOnce = new WeakSet();

  let requestInFlight = null;
  let checkoutBusy = false;
  let noticeTimer = 0;
  let pendingCheckout = false;
  let pendingResume = null;
  let intentVersion = 0;

  function cancelCheckoutIntent() {
    pendingCheckout = false;
    pendingResume = null;
    intentVersion++;
    removeStorage(RETURN_KEY);
  }

  function readStorage(key) {
    try {
      return sessionStorage.getItem(key) || '';
    } catch {
      return '';
    }
  }

  function writeStorage(key, value) {
    try {
      sessionStorage.setItem(key, value);
    } catch {}
  }

  function removeStorage(key) {
    try {
      sessionStorage.removeItem(key);
    } catch {}
  }

  function saveCheckoutIntent() {
    writeStorage(RETURN_KEY, JSON.stringify({destination: 'checkout', createdAt: Date.now()}));
  }

  function readCheckoutIntent() {
    const raw = readStorage(RETURN_KEY);
    if (!raw) return false;

    try {
      const intent = JSON.parse(raw);
      const age = Date.now() - Number(intent.createdAt);
      if (intent.destination === 'checkout' && age >= 0 && age <= RETURN_MAX_AGE_MS) return true;
    } catch {}

    removeStorage(RETURN_KEY);
    return false;
  }

  function loginPageRequestsCheckout() {
    return location.pathname === LOGIN_PATH && new URLSearchParams(location.search).get('next') === 'checkout';
  }

  function checkoutUrl(value) {
    if (!value) return false;

    try {
      const url = new URL(value, location.origin);
      return url.origin === location.origin && (/^\/checkout(?:\/|$)/.test(url.pathname) || /^\/checkouts(?:\/|$)/.test(url.pathname));
    } catch {
      return false;
    }
  }

  function eventPath(event) {
    if (typeof event.composedPath === 'function') return event.composedPath();
    return event.target ? [event.target] : [];
  }

  function findCheckoutControl(event) {
    const path = eventPath(event);
    const fromSmsAccount = path.some((node) => node instanceof Element && node.tagName === 'KID-SMS-ACCOUNT');

    for (const node of path) {
      if (!(node instanceof Element)) continue;
      if (node.matches('button[name="checkout"], input[name="checkout"]')) return node;
      if (node.matches('a[href]') && checkoutUrl(node.getAttribute('href'))) return node;
      if (fromSmsAccount && node.matches('button.checkout')) return node;
    }

    return null;
  }

  function showNotice(message) {
    const dialog = document.querySelector('kid-sms-account[data-layout="modal"]')?.shadowRoot?.querySelector('dialog[open]');
    const container = dialog || document.body || document.documentElement;
    let notice = container.querySelector('[data-kid-sms-checkout-notice]');
    if (!notice) {
      notice = document.createElement('div');
      notice.setAttribute('data-kid-sms-checkout-notice', '');
      notice.setAttribute('role', 'alert');
      notice.setAttribute('aria-live', 'assertive');
      notice.style.cssText = [
        'position:fixed',
        'left:50%',
        'bottom:24px',
        'z-index:2147483647',
        'max-width:min(92vw,520px)',
        'transform:translateX(-50%)',
        'padding:12px 16px',
        'border-radius:12px',
        'background:#7f1d1d',
        'color:#fff',
        'font:600 15px/1.5 Anuphan,system-ui,sans-serif',
        'box-shadow:0 12px 32px rgba(0,0,0,.24)',
        'text-align:center'
      ].join(';');
      container.appendChild(notice);
    }

    notice.textContent = message;
    notice.hidden = false;
    window.clearTimeout(noticeTimer);
    noticeTimer = window.setTimeout(() => {
      notice.hidden = true;
    }, 7000);
  }

  async function validateSession() {
    const token = readStorage(TOKEN_KEY);
    if (!/^[a-f0-9]{64}$/.test(token)) {
      if (token) removeStorage(TOKEN_KEY);
      return false;
    }

    if (requestInFlight) return requestInFlight;

    requestInFlight = (async () => {
      const controller = new AbortController();
      const timer = window.setTimeout(() => controller.abort(), 12000);

      try {
        const local = ['localhost', '127.0.0.1', '[::1]'].includes(location.hostname) && document.querySelector('kid-sms-account[data-local]');
        const response = await fetch(local ? '/api/storefront/session/status' : SESSION_URL, {
          method: 'POST',
          headers: {'Content-Type': 'application/json', ...(local ? {'X-Kid-Local': '1'} : {})},
          body: JSON.stringify({token}),
          cache: 'no-store',
          credentials: 'same-origin',
          signal: controller.signal
        });

        if (response.ok) return readStorage(TOKEN_KEY) === token;
        if (response.status === 401 || response.status === 403) {
          if (readStorage(TOKEN_KEY) === token) removeStorage(TOKEN_KEY);
          return false;
        }

        throw new Error('session_check_failed');
      } catch (error) {
        if (error instanceof Error && error.message === 'session_check_failed') throw error;
        throw new Error('network_error');
      } finally {
        window.clearTimeout(timer);
      }
    })().finally(() => {
      requestInFlight = null;
    });

    return requestInFlight;
  }

  function redirectToSmsLogin(control, form, submitter) {
    saveCheckoutIntent();
    pendingCheckout = true;
    pendingResume = {control, form, submitter};
    intentVersion++;
    const modal = document.querySelector('kid-sms-account[data-layout="modal"]');
    if (modal && typeof modal.open === 'function') {
      void Promise.resolve(modal.open({intent: 'checkout', trigger: control})).catch(() => {
        cancelCheckoutIntent();
        showNotice('เปิดฟอร์มเข้าสู่ระบบไม่ได้ กรุณาลองอีกครั้ง');
      });
      return;
    }
    const url = new URL(LOGIN_PATH, location.origin);
    url.searchParams.set('next', 'checkout');
    location.assign(`${url.pathname}${url.search}`);
  }

  function resumeCheckout(control, form, submitter) {
    removeStorage(RETURN_KEY);

    if (control instanceof HTMLElement) {
      allowedOnce.add(control);
      if (control.form) allowedOnce.add(control.form);
      try {
        control.click();
      } finally {
        allowedOnce.delete(control);
        if (control.form) allowedOnce.delete(control.form);
      }
      return;
    }

    if (form instanceof HTMLFormElement) {
      allowedOnce.add(form);
      try {
        form.requestSubmit(submitter || undefined);
      } finally {
        allowedOnce.delete(form);
      }
    }
  }

  async function guardCheckout(control, form, submitter) {
    if (checkoutBusy) return;
    checkoutBusy = true;
    control?.setAttribute?.('aria-busy', 'true');

    try {
      if (await validateSession()) {
        resumeCheckout(control, form, submitter);
      } else {
        redirectToSmsLogin(control, form, submitter);
      }
    } catch {
      showNotice('ตรวจสอบสถานะสมาชิกไม่ได้ กรุณาตรวจสอบอินเทอร์เน็ตแล้วลองอีกครั้ง');
    } finally {
      control?.removeAttribute?.('aria-busy');
      checkoutBusy = false;
    }
  }

  async function continuePendingCheckout() {
    if (!pendingCheckout || !readCheckoutIntent() || checkoutBusy) return;
    const version = intentVersion;
    checkoutBusy = true;

    try {
      if (await validateSession() && pendingCheckout && version === intentVersion) {
        const original = pendingResume;
        cancelCheckoutIntent();
        document.querySelector('kid-sms-account[data-layout="modal"]')?.close?.('checkout');
        if (original) {
          if ((original.control && !original.control.isConnected) ||
              (!original.control && !original.form?.isConnected)) {
            showNotice('เข้าสู่ระบบแล้ว กรุณากดปุ่ม Checkout อีกครั้ง');
            return;
          }
          resumeCheckout(original.control?.isConnected ? original.control : null,
            original.form?.isConnected ? original.form : null,
            original.submitter?.isConnected ? original.submitter : null);
        } else {
          location.replace(CHECKOUT_PATH);
        }
      }
    } catch {
      showNotice('ยืนยันสถานะสมาชิกแล้ว แต่ยังเชื่อมต่อ Checkout ไม่ได้ กรุณาลองอีกครั้ง');
    } finally {
      checkoutBusy = false;
    }
  }

  document.addEventListener('click', (event) => {
    const path = eventPath(event);
    const storeBack = path.find((node) => node instanceof Element && node.matches('a.store-back'));
    if (storeBack) cancelCheckoutIntent();

    const control = findCheckoutControl(event);
    if (!control) return;
    if (allowedOnce.has(control)) {
      allowedOnce.delete(control);
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();
    void guardCheckout(control, control.form || null, control);
  }, true);

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (allowedOnce.has(form)) {
      allowedOnce.delete(form);
      return;
    }

    const submitter = event.submitter;
    if (!(submitter instanceof HTMLElement) || submitter.getAttribute('name') !== 'checkout') return;

    event.preventDefault();
    event.stopImmediatePropagation();
    void guardCheckout(submitter, form, submitter);
  }, true);

  document.addEventListener('kid-account:opened', (event) => {
    if (event.detail?.intent !== 'checkout') {
      cancelCheckoutIntent();
      return;
    }
    pendingCheckout = true;
    intentVersion++;
    saveCheckoutIntent();
    if (readStorage(TOKEN_KEY)) void continuePendingCheckout();
  });
  document.addEventListener('kid-account:closed', cancelCheckoutIntent);
  document.addEventListener('kid-account:logout', cancelCheckoutIntent);
  document.addEventListener('kid-account:login', (event) => {
    if (event.detail?.modalOpen === false || event.detail?.intent === 'account') return;
    void continuePendingCheckout();
  });

  if (loginPageRequestsCheckout()) {
    pendingCheckout = true;
    if (!readCheckoutIntent()) saveCheckoutIntent();
    if (readStorage(TOKEN_KEY)) void continuePendingCheckout();
  }
})();
